<template>
  <div>
    <b-card>
      <div slot="header">
        {{ title }}
      </div>
      <b-row>
        <b-col>
          <LoadingSpinner :loading="parentLoading" :text="$lang.sending_data" />
        </b-col>
      </b-row>
      <b-row v-if="!parentLoading">
        <b-col cols="12" md="12" v-if="alertInformation">
          <b-alert show variant="info">
            <i class="fa fa-check-circle fa-lg mr-1" />
            <b v-text="alertInformation" />
          </b-alert>
        </b-col>
        <b-col cols="12">
          <b-form-group>
            <requiredComponent
              slot="label"
              :label="researchManagerLabel || $lang.research_manager"
              :rules="$v.from_relation_person_code_selected.$params"
            />
            <selectAsync
              :options="from_relation_person_code_options"
              v-model="$v.from_relation_person_code_selected.$model"
              label="text"
              id="value"
              :asyncFind="getFromRelationPersonCode"
              :loading="loading.from_relation_person_code"
              :isValid="
                $v.from_relation_person_code_selected.$dirty
                  ? !$v.from_relation_person_code_selected.$error
                  : null
              "
            />
            <b-form-invalid-feedback
              :style="{
                display: $v.from_relation_person_code_selected.$error
                  ? 'block'
                  : 'none',
              }"
              id="from_relation_person_code_selected_feedback"
            >
              {{
                $lang.error_required
                  | replace(researchManagerLabel || $lang.research_manager)
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group>
            <requiredComponent
              slot="label"
              :label="researcherLabel || $lang.researcher"
              :rules="$v.assessor_selected.$params"
            />
            <selectAsync
              :options="assessor_options"
              v-model="$v.assessor_selected.$model"
              label="text"
              id="value"
              :multiple="multiple"
              :asyncFind="getAssessor"
              :loading="loading.assessor"
              :isValid="
                $v.assessor_selected.$dirty
                  ? !$v.assessor_selected.$error
                  : null
              "
            />
            <b-form-invalid-feedback
              :style="{
                display: $v.assessor_selected.$error ? 'block' : 'none',
              }"
              id="assessor_feedback"
            >
              {{
                $lang.error_required
                  | replace(researcherLabel || $lang.researcher)
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12" class="text-right">
          <b-button
            variant="success"
            @click="submit"
            v-if="isAdmin(parentPermissions)"
          >
            {{ $lang.submitButton }}
            <i class="fa fa-check"></i>
          </b-button>
          <b-button
            type="reset"
            class="ml-2"
            size="md"
            variant="danger"
            @click="closeModal"
          >
            {{ $lang.cancelButton }}
            <i class="fa fa-ban"></i>
          </b-button>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import LoadingSpinner from "@/components/base/LoadingSpinner";
import { isAdmin } from "@/shared/utils";

export default {
  components: { LoadingSpinner },
  validations: {
    from_relation_person_code_selected: {
      required,
    },
    assessor_selected: {
      required,
    },
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    researchManagerLabel: {
      type: String,
      default: null,
    },
    researcherLabel: {
      type: String,
      default: null,
    },
    parentLoading: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    alertInformation: {
      type: String,
      default: null,
    },
    parentPermissions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loadingItem: false,
      loading: {
        from_relation_person_code: false,
        assessor: false,
      },
      data: {
        from_relation_person_code: [],
        assessor: [],
      },

      assessor_selected: [],
      from_relation_person_code_selected: {},
    };
  },
  computed: {
    from_relation_person_code_options() {
      return this.data.from_relation_person_code.map((x) => {
        return {
          text: `${pathOr("", ["parvandeh", "parvandeh_number"], x)} - ${pathOr(
            "",
            ["person", "name"],
            x
          )} ${pathOr("", ["person", "family"], x)} - ${pathOr(
            "",
            ["person", "national_code"],
            x
          )} - ${pathOr("", ["parvandeh", "cooperation", "caption"], x)}`,
          value: pathOr("", ["parvandeh", "code"], x),
        };
      });
    },
    assessor_options() {
      return this.data.assessor.map((x) => {
        return {
          text: `${pathOr("", ["parvandeh", "parvandeh_number"], x)} - ${pathOr(
            "",
            ["person", "name"],
            x
          )} ${pathOr("", ["person", "family"], x)} - ${pathOr(
            "",
            ["person", "national_code"],
            x
          )} - ${pathOr("", ["parvandeh", "cooperation", "caption"], x)}`,
          value: pathOr("", ["parvandeh", "code"], x),
        };
      });
    },
  },
  methods: {
    pathOr,
    isAdmin,
    submit() {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }

      let payload = {
        research_manager_code: pathOr(
          null,
          ["value"],
          this.from_relation_person_code_selected
        ),
        researcher_code: this.multiple
          ? this.assessor_selected.map((x) => x.value)
          : pathOr(null, ["value"], this.assessor_selected),
      };

      this.$emit("submit", payload);
    },
    closeModal() {
      this.$emit("closeModal");
    },
    async getCombo(key, searchText = "") {
      try {
        this.loading[key] = true;
        const url =
          this.$config.api_url +
          this.$config.research.research_person_combo.replace(
            "{1}",
            searchText
          );
        const response = await this.$httpService.get(url);
        this.data[key] = [...pathOr([], ["data", "data"], response)];
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loading[key] = false;
      }
    },
    getFromRelationPersonCode(query) {
      if (!query || query.length < 2) {
        return;
      }
      this.getCombo("from_relation_person_code", query);
    },
    getAssessor(query) {
      if (!query || query.length < 2) {
        return;
      }
      this.getCombo("assessor", query);
    },
  },
};
</script>

<style>
.f-15 {
  font-size: 15px;
}
</style>
